import { createTheme } from '@mui/material/styles';
import { getGradientBackgroundStyle } from './utils/get-gradient-background-style';

const globalTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0D9488',
      light: '#5EEAD4',
      dark: '#0F766E',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#0F766E',
      light: '#CCFBF1',
      mid: '#14B8A6',
      contrastText: '#FFFFFF',
    },
    tertiary: {
      main: '#0D9488',
      light: '#CCFBF1',
    },
    error: {
      main: '#DC2626',
    },
    warning: {
      main: '#EA580C',
    },
    invalid: {
      main: '#DC2626',
    },
    darkTheme: {
      main: '#0F172A',
      card: '#1E293B',
    },
    common: {
      light: '#FFFFFF',
      dark: '#0F172A',
      black: '#000000',
      pink: '#FB7185',
      orangelt: '#FB923C',
    },
    background: {
      default: '#F8FAF8',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F172A',
      secondary: '#64748B',
    },
    divider: '#E2E8F0',
  },
  typography: {
    fontFamily: [
      '"DM Sans"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'sans-serif',
    ].join(','),
  },
  shape: {
    borderRadius: 12,
  },
});

const theme = createTheme(
  {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: '#F8FAF8',
          },
        },
      },
      MuiSvgIcon: {
        variants: [
          {
            props: {
              variant: 'invalid',
            },
            style: {
              color: '#DC2626',
            },
          },
        ],
      },
      MuiPaper: {
        variants: [
          {
            props: {
              variant: 'gradient',
            },
            style: {
              background: getGradientBackgroundStyle(globalTheme),
              color: '#FFFFFF',
              borderRadius: '0',
              border: 'none',
              [globalTheme.breakpoints.up('md')]: {
                borderRadius: '1.25rem',
              },
              '.MuiTypography-root': {
                color: '#FFFFFF',
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            backgroundColor: '#FFFFFF',
            color: globalTheme.palette.text.primary,
            backgroundImage: 'none',
            border: '1px solid #E2E8F0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            color: globalTheme.palette.text.primary,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            textTransform: 'none',
            fontFamily: '"Outfit", "DM Sans", sans-serif',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            transition: 'all 0.2s ease',
          },
        },
        variants: [
          {
            props: {
              variant: 'contained',
            },
            style: {
              background: globalTheme.palette.primary.main,
              color: '#FFFFFF',
              fontSize: '1rem',
              fontWeight: 600,
              lineHeight: '1.5em',
              padding: '0.5em 1.25em',
              boxShadow: '0 1px 3px rgba(13,148,136,0.3)',
              '&:hover': {
                backgroundColor: globalTheme.palette.primary.dark,
                boxShadow: '0 4px 12px rgba(13,148,136,0.25)',
                transform: 'translateY(-1px)',
              },
            },
          },
          {
            props: {
              variant: 'outlined',
            },
            style: {
              fontSize: '1rem',
              fontWeight: 600,
              lineHeight: '1.5em',
              padding: '0.5em 1.25em',
              borderColor: '#E2E8F0',
              color: globalTheme.palette.text.primary,
              '&:hover': {
                borderColor: globalTheme.palette.primary.main,
                backgroundColor: '#F0FDFA',
              },
            },
          },
          {
            props: {
              variant: 'text',
            },
            style: {
              padding: 0,
              fontSize: '1em',
              fontWeight: 600,
              color: globalTheme.palette.primary.main,
              '&:hover': {
                backgroundColor: 'transparent',
                opacity: 0.8,
              },
            },
          },
          {
            props: {
              variant: 'text-dark',
            },
            style: {
              padding: 0,
              fontSize: '1em',
              fontWeight: 600,
              color: globalTheme.palette.text.primary,
              '&:hover': {
                backgroundColor: 'transparent',
                opacity: 0.7,
              },
            },
          },
        ],
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            '&.progress-signup': {
              background: '#E2E8F0',
              borderRadius: '10px',
              height: '8px',
              '.MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #0D9488 0%, #5EEAD4 100%)',
                borderRadius: '10px',
              },
            },
          },
        },
      },
      MuiFormControlLabel: {
        variants: [
          {
            props: {
              variant: 'lightPill',
            },
            style: {
              backgroundColor: '#FFFFFF',
              borderWidth: '1px',
              borderRadius: '12px',
              borderStyle: 'solid',
              display: 'block',
              borderColor: '#E2E8F0',
              textAlign: 'left',
              padding: '1em 1.5em',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: globalTheme.palette.primary.main,
                backgroundColor: '#F0FDFA',
              },
              '&.checked': {
                borderColor: globalTheme.palette.primary.main,
                backgroundColor: '#F0FDFA',
              },
              '& .MuiTypography-root': {
                color: globalTheme.palette.text.primary,
                fontSize: '1.125em',
                fontWeight: '600',
                margin: '0',
              },
            },
          },
        ],
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: '#FFFFFF',
            border: '1px solid #E2E8F0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
            borderRadius: '16px',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.03)',
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: '16px',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: '#FFFFFF',
            backgroundImage: 'none',
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            color: globalTheme.palette.text.primary,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E2E8F0',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: globalTheme.palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: globalTheme.palette.primary.main,
              borderWidth: '2px',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: '#64748B',
          },
        },
      },
      MuiCalendarPicker: {
        styleOverrides: {
          root: {
            backgroundColor: '#FFFFFF',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: '"Outfit", "DM Sans", sans-serif',
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '0.95rem',
            color: '#64748B',
            '&.Mui-selected': {
              color: globalTheme.palette.primary.main,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: globalTheme.palette.primary.main,
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '12px !important',
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: '0',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontFamily: '"Outfit", "DM Sans", sans-serif',
            fontWeight: 600,
            color: '#64748B',
            fontSize: '0.8rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottomColor: '#E2E8F0',
          },
          body: {
            borderBottomColor: '#F1F5F9',
            color: globalTheme.palette.text.primary,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.15s ease',
            '&:hover': {
              backgroundColor: '#F8FAFC',
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: '#E2E8F0',
          },
        },
      },
      MuiTypography: {
        variants: [
          {
            props: {
              variant: 'p',
            },
            style: {
              fontSize: '1.05rem',
              color: globalTheme.palette.text.primary,
              lineHeight: 1.6,
            },
          },
          {
            props: {
              variant: 'h1',
            },
            style: {
              fontFamily: '"Outfit", "DM Sans", sans-serif',
              [globalTheme.breakpoints.up('md')]: {
                fontSize: 48,
              },
              fontSize: 32,
              color: globalTheme.palette.text.primary,
              fontWeight: 700,
              letterSpacing: '-0.03em',
              lineHeight: 1.15,
            },
          },
          {
            props: {
              variant: 'h2',
            },
            style: {
              fontFamily: '"Outfit", "DM Sans", sans-serif',
              fontSize: 24,
              color: globalTheme.palette.text.primary,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
            },
          },
          {
            props: {
              variant: 'h3',
            },
            style: {
              fontFamily: '"Outfit", "DM Sans", sans-serif',
              fontSize: 18,
              color: globalTheme.palette.text.primary,
              fontWeight: 500,
            },
          },
          {
            props: {
              variant: 'h4',
            },
            style: {
              fontFamily: '"Outfit", "DM Sans", sans-serif',
              fontSize: 22,
              color: globalTheme.palette.text.primary,
              fontWeight: 600,
              letterSpacing: '-0.01em',
            },
          },
          {
            props: {
              variant: 'h5',
            },
            style: {
              fontFamily: '"Outfit", "DM Sans", sans-serif',
              fontSize: 'clamp(1.25rem, 2vw + 0.5rem, 2rem)',
              color: globalTheme.palette.text.primary,
              lineHeight: 1.3,
            },
          },
          {
            props: {
              variant: 'h6',
            },
            style: {
              fontFamily: '"Outfit", "DM Sans", sans-serif',
              fontSize: 18,
              color: globalTheme.palette.primary.main,
              fontWeight: 600,
            },
          },
          {
            props: {
              variant: 'h7',
            },
            style: {
              fontSize: 18,
              color: globalTheme.palette.text.primary,
              fontWeight: 400,
            },
          },
          {
            props: {
              variant: 'subtitle1',
            },
            style: {
              fontSize: '0.875rem',
              color: globalTheme.palette.text.secondary,
              fontWeight: 400,
              lineHeight: 1.5,
            },
          },
          {
            props: {
              variant: 'subtitle2',
            },
            style: {
              fontSize: 15,
              color: globalTheme.palette.primary.main,
              fontWeight: 500,
            },
          },
          {
            props: {
              variant: 'body1',
            },
            style: {
              fontSize: 15,
              color: globalTheme.palette.text.primary,
              fontWeight: 400,
              lineHeight: 1.6,
            },
          },
        ],
      },
    },
  },
  globalTheme
);

export default theme;
